/**
 * Determine the response `content-type` we should return for an export artifact,
 * favouring any explicit metadata stored alongside the artifact.
 */
export function resolveContentType(format?: string, existing?: string): string {
  if (existing && existing !== 'application/octet-stream') {
    return existing;
  }

  switch ((format ?? '').toLowerCase()) {
    case 'png':
      return 'image/png';
    case 'jpeg':
    case 'jpg':
      return 'image/jpeg';
    case 'pdf':
      return 'application/pdf';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'csv':
      return 'text/csv; charset=utf-8';
    case 'json':
      return 'application/json; charset=utf-8';
    default:
      return 'image/svg+xml';
  }
}

/**
 * Pick an output file extension based on either the requested export format or
 * the stored artifact content type.
 */
export function resolveExtension(format?: string, contentType?: string | null): string {
  const normalizedFormat = format?.toLowerCase();
  if (normalizedFormat === 'png') return 'png';
  if (normalizedFormat === 'jpeg' || normalizedFormat === 'jpg') return 'jpeg';
  if (normalizedFormat === 'pdf') return 'pdf';
  if (normalizedFormat === 'docx') return 'docx';
  if (normalizedFormat === 'csv') return 'csv';
  if (normalizedFormat === 'json') return 'json';

  const normalizedType = contentType?.toLowerCase();
  if (normalizedType === 'image/png') return 'png';
  if (normalizedType === 'image/jpeg') return 'jpeg';
  if (normalizedType === 'application/pdf') return 'pdf';
  if (normalizedType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx';
  if (normalizedType?.includes('text/csv')) return 'csv';
  if (normalizedType?.includes('application/json')) return 'json';

  return 'svg';
}

/**
 * Build the `content-disposition` header used by both worker and client to keep
 * filenames consistent across download flows.
 */
export interface DownloadDispositionOptions {
  reportType?: string;
  reportVariant?: string;
}

export function buildDownloadDisposition(
  diagramId: string,
  format?: string,
  contentType?: string | null,
  options?: DownloadDispositionOptions,
): string {
  const extension = resolveExtension(format, contentType);
  const dispositionType = extension === 'svg' ? 'inline' : 'attachment';
  const suffix = buildNameSuffix(options);
  const baseName = suffix ? `${diagramId}-${suffix}` : diagramId;
  return `${dispositionType}; filename="${baseName}.${extension}"`;
}

function buildNameSuffix(options?: DownloadDispositionOptions): string | undefined {
  if (!options?.reportType) return options?.reportVariant?.trim() || undefined;

  const normalizedType = options.reportType.replace(/\s+/g, '-').replace(/[^\w.-]/g, '').replace(/\.+/g, '.');
  const normalizedVariant = options.reportVariant?.trim().replace(/\s+/g, '-').replace(/[^\w.-]/g, '');

  if (normalizedVariant) {
    return `${normalizedType}-${normalizedVariant}`;
  }

  return normalizedType;
}
